import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

export default function CancelConfirm() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [state, setState] = useState('loading'); // loading | form | submitting | done | expired | already | error
  const [errorMsg, setErrorMsg] = useState('');
  const [info, setInfo] = useState(null); // { firstName, lastName, flightLabel, route, deadline }
  const [bookingRef, setBookingRef] = useState('');
  const [passportNum, setPassportNum] = useState('');
  const [last4, setLast4] = useState('');
  const [fieldError, setFieldError] = useState('');

  useEffect(() => {
    if (!token) {
      setState('error');
      setErrorMsg('Invalid link — missing cancellation token.');
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_API_URL}/flight/cancel-info?token=${encodeURIComponent(token)}`);
        const data = await res.json();

        if (data.status === 'ok') {
          setInfo(data);
          setState('form');
        } else if (data.status === 'expired') {
          setState('expired');
          setErrorMsg(data.message);
        } else if (data.status === 'already_confirmed') {
          setState('already');
        } else {
          setState('error');
          setErrorMsg(data.message || 'Invalid cancellation link.');
        }
      } catch (err) {
        setState('error');
        setErrorMsg(err.message || 'Network error. Please try again.');
      }
    })();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFieldError('');

    if (!bookingRef.trim()) { setFieldError('Booking reference is required.'); return; }
    if (!passportNum.trim()) { setFieldError('Passport number is required.'); return; }

    setState('submitting');
    try {
      const res = await fetch(`${import.meta.env.VITE_API_URL}/flight/cancel-confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, bookingReference: bookingRef.trim(), passportNumber: passportNum.trim(), last4: last4.trim() })
      });
      const data = await res.json();

      if (data.status === 'success') {
        setState('done');
      } else if (data.status === 'expired') {
        setState('expired');
        setErrorMsg(data.message);
      } else if (data.status === 'already_confirmed') {
        setState('already');
      } else {
        setFieldError(data.message || 'Verification failed. Please check your details.');
        setState('form');
      }
    } catch (err) {
      setFieldError(err.message || 'Network error. Please try again.');
      setState('form');
    }
  };

  const deadlineStr = info?.deadline
    ? new Date(info.deadline).toLocaleString('en-US', { timeZone: 'Asia/Jerusalem', weekday: 'long', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    : null;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-lg overflow-hidden">
        {/* Header */}
        <div className="bg-blue-700 px-6 py-5 text-center">
          <div className="flex items-center justify-center gap-4 mb-3">
            <img src="/igud-logo.png" alt="Igud Yeshivos" className="h-10 w-auto" />
            <img src="/ehk-logo_2-1.png" alt="Eretz Hakodesh" className="h-10 w-auto" />
          </div>
          <h1 className="text-white font-bold text-xl">Charter Flight</h1>
        </div>

        <div className="px-6 py-8">
          {/* Loading */}
          {state === 'loading' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading...</p>
            </div>
          )}

          {/* Form */}
          {state === 'form' && info && (
            <div>
              <h2 className="text-xl font-bold text-gray-800 mb-2 text-center">Confirm Cancellation</h2>
              <p className="text-gray-600 text-sm mb-4 text-center">
                {info.firstName} {info.lastName} — {info.flightLabel}
                <br /><span className="text-gray-500">{info.route}</span>
              </p>

              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-5 text-sm text-red-700 space-y-2">
                <p><strong>Please note:</strong> Tickets are officially non-refundable. If you confirm your cancellation, we will release your seat and attempt to resell it{deadlineStr ? <> before the ticket confirmation deadline (<strong>{deadlineStr} Israel time</strong>)</> : null}.</p>
                <ul className="list-disc pl-4 space-y-1">
                  {deadlineStr && <li>We must confirm all tickets with the airline by <strong>{deadlineStr} Israel time</strong>. After that point, we cannot offer any refunds.</li>}
                  <li>Your seat can only be resold <strong>before</strong> that deadline — there is <strong>no guarantee</strong> it will be resold in time.</li>
                  <li>If your seat is not resold by the deadline, you will <strong>not</strong> receive a refund.</li>
                  <li>If your seat is resold, a <strong>$150 processing fee</strong> will be deducted from your refund.</li>
                </ul>
              </div>

              {fieldError && (
                <div className="bg-red-100 border border-red-300 rounded-lg p-3 mb-4 text-sm text-red-800">
                  {fieldError}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Booking Reference</label>
                  <input
                    type="text"
                    value={bookingRef}
                    onChange={(e) => setBookingRef(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="e.g. ABC12345"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Passport Number</label>
                  <input
                    type="text"
                    value={passportNum}
                    onChange={(e) => setPassportNum(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Passport number used at registration"
                    autoComplete="off"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last 4 Digits of Card</label>
                  <input
                    type="text"
                    value={last4}
                    onChange={(e) => setLast4(e.target.value.replace(/\D/g, '').slice(0, 4))}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="1234"
                    maxLength={4}
                    inputMode="numeric"
                    autoComplete="off"
                  />
                </div>

                <button
                  type="submit"
                  className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg transition-colors"
                >
                  Confirm Cancellation
                </button>
              </form>
            </div>
          )}

          {/* Submitting */}
          {state === 'submitting' && (
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-400 mx-auto mb-4"></div>
              <p className="text-gray-600">Processing...</p>
            </div>
          )}

          {/* Done */}
          {state === 'done' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x2705;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Cancellation Confirmed</h2>
              <p className="text-gray-600">
                Your ticket has been released. If your seat is purchased by someone else before the ticket confirmation deadline, you will receive a refund minus a $150 processing fee.
              </p>
              <p className="text-red-600 font-medium text-sm mt-2">There is no guarantee that your seat will be resold in time. If it is not resold by the deadline, no refund will be issued.</p>
              <p className="text-gray-500 text-sm mt-4">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {/* Expired */}
          {state === 'expired' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x23F0;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Deadline Passed</h2>
              <p className="text-gray-600">{errorMsg || 'The cancellation window for this flight has closed. Your ticket remains valid.'}</p>
              <p className="text-gray-500 text-sm mt-4">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {/* Already confirmed */}
          {state === 'already' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x2705;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Already Cancelled</h2>
              <p className="text-gray-600">You have already confirmed your cancellation. No further action is needed.</p>
              <p className="text-gray-500 text-sm mt-4">
                Questions? <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}

          {/* Error */}
          {state === 'error' && (
            <div className="text-center">
              <div className="text-4xl mb-4">&#x26A0;&#xFE0F;</div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">Something Went Wrong</h2>
              <p className="text-gray-600 mb-4">{errorMsg}</p>
              <p className="text-gray-500 text-sm">
                Please contact us at{' '}
                <a href="mailto:flights@igudys.com" className="text-blue-600 underline">flights@igudys.com</a>
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
